import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlignCenter, Dumbbell, Wind, Sparkles, CalendarCheck } from "lucide-react";
import { Reveal } from "./Reveal";
import { WA_SCHEDULE } from "@/lib/constants";

const CARDS = [
  { icon: AlignCenter, title: "Postura", text: "Melhora do alinhamento e da consciência corporal no dia a dia." },
  { icon: Dumbbell, title: "Fortalecimento", text: "Força e estabilidade com exercícios controlados e orientados." },
  { icon: Wind, title: "Flexibilidade", text: "Movimentos que trazem leveza e amplitude para o corpo." },
  { icon: Sparkles, title: "Bem-estar", text: "Mais energia, equilíbrio e qualidade de vida na sua rotina." },
];

const PHOTOS = [
  { src: "/pilates/pilates-1.jpg", alt: "Entrada do Studio Pilates da Clínica COB" },
  { src: "/pilates/pilates-2.jpg", alt: "Studio Pilates da Clínica COB com maca e tapetes" },
  { src: "/pilates/pilates-3.jpg", alt: "Espaço do Studio Pilates com a frase o movimento cura" },
  { src: "/pilates/pilates-4.jpg", alt: "Equipamentos de pilates do Studio da Clínica COB" },
];

export const Pilates = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setActive((i) => (i + 1) % PHOTOS.length), 4500);
    return () => clearInterval(t);
  }, [paused]);

  return (
    <section id="pilates" data-testid="pilates-section" className="bg-cob-beige/50 py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-[2.5rem] border border-cob-light bg-white px-6 py-14 sm:px-12 lg:px-16 lg:py-20 shadow-[0_20px_60px_-35px_rgba(6,63,50,0.25)]">
          <div className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full border border-cob-gold/25" />
          <div className="pointer-events-none absolute -bottom-40 -right-24 h-96 w-96 rounded-full bg-cob-light/40 blur-3xl" />

          <div className="relative grid lg:grid-cols-2 gap-12 items-center">
            <Reveal className="relative order-2 lg:order-1">
              <div
                className="relative mx-auto max-w-md lg:max-w-none"
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
              >
                <div className="absolute -inset-2.5 rounded-[1.8rem] border border-cob-gold/40 -translate-x-3 translate-y-3 pointer-events-none" />
                <div className="relative h-[340px] sm:h-[420px] overflow-hidden rounded-[1.6rem] shadow-[0_25px_60px_-25px_rgba(6,63,50,0.4)]">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={PHOTOS[active].src}
                      src={PHOTOS[active].src}
                      alt={PHOTOS[active].alt}
                      data-testid="pilates-image"
                      loading="lazy"
                      decoding="async"
                      initial={{ opacity: 0, scale: 1.05 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  </AnimatePresence>
                </div>
                <div className="mt-4 flex justify-center gap-2.5">
                  {PHOTOS.map((photo, i) => (
                    <button
                      key={photo.src}
                      type="button"
                      data-testid={`pilates-thumb-${i + 1}`}
                      aria-label={`Ver foto ${i + 1} do Studio Pilates`}
                      onClick={() => setActive(i)}
                      className={`h-14 w-14 overflow-hidden rounded-xl transition-all duration-300 ${
                        i === active
                          ? "ring-2 ring-cob-gold ring-offset-2 ring-offset-white scale-105"
                          : "opacity-55 hover:opacity-90"
                      }`}
                    >
                      <img loading="lazy" decoding="async" src={photo.src} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            </Reveal>

            <div className="order-1 lg:order-2">
              <Reveal>
                <p className="text-xs font-bold uppercase tracking-[0.28em] text-cob-gold">Studio Pilates</p>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-[2.9rem] font-semibold tracking-tight leading-[1.1] text-cob-dark">
                  Força, equilíbrio e <span className="italic text-cob-green">consciência corporal.</span>
                </h2>
              </Reveal>
              <Reveal delay={0.16}>
                <p className="mt-5 max-w-lg text-base sm:text-lg leading-relaxed text-cob-muted">
                  No Studio Pilates da Clínica COB, cada aula é guiada para fortalecer o corpo,
                  melhorar a postura e trazer mais equilíbrio para o seu dia a dia.
                </p>
              </Reveal>
              <Reveal delay={0.24}>
                <a
                  href={WA_SCHEDULE}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pilates-cta-whatsapp"
                  className="btn-press mt-8 inline-flex items-center gap-2.5 rounded-full bg-cob-green px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-cob-dark hover:shadow-[0_16px_35px_-12px_rgba(6,63,50,0.5)]"
                >
                  <CalendarCheck className="h-4.5 w-4.5" />
                  Agendar aula experimental
                </a>
              </Reveal>
            </div>
          </div>

          <div className="relative mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {CARDS.map((card, i) => (
              <Reveal key={card.title} delay={0.1 + i * 0.08} className="h-full">
                <div
                  data-testid={`pilates-card-${i + 1}`}
                  className="group h-full rounded-2xl border border-cob-light bg-cob-beige/40 p-6 transition-all duration-500 hover:border-cob-gold/50 hover:bg-white hover:shadow-[0_18px_40px_-22px_rgba(6,63,50,0.3)]"
                >
                  <card.icon className="h-6 w-6 text-cob-gold transition-transform duration-500 group-hover:-rotate-6 group-hover:scale-110" strokeWidth={1.7} />
                  <h3 className="mt-4 font-serif text-xl font-semibold text-cob-dark">{card.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-cob-muted">{card.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
